import React, { useEffect, useRef, useState } from 'react';
import { connect } from 'react-redux';
import { push } from 'connected-react-router';
import { AppState } from 'reducers/reducers';
import { selectedSpotId } from 'selectors';
import { trackPageViewed } from 'utils/analytics';
import { useOrganization } from 'contexts/OrganizationsContext';
import AGButton from 'components/Button/Button';
import AGTAbleWrapper from 'components/AGTable/AGTableWrapper';
import styles from './UploadVisitorsCSVModuleContainer.module.css';
import AGMessage from 'components/AGMessage/AGMessage';
import TextInput from 'components/AGForm/Inputs/TextInput/TextInput';
import AGForm from 'components/AGForm/AGForm';
import { useVisitors } from 'contexts/VisitorsContext';
import CheckCircleIcon from 'components/Icons/CheckCircleIcon';
import FileIcon from 'components/Icons/FileIcon';
import UploadIcon from 'components/Icons/UploadIcon';
import XIcon from 'components/Icons/XIcon';
import ArrowIcon from 'components/Icons/ArrowIcon';
import ProgressBar from 'components/ProgressBar/ProgressBar';

type UploadVisitorsCSVModuleContainerProps = ReturnType<typeof mapStateToProps> & typeof mapDispatchToProps;
const UploadVisitorsCSVModuleContainer = ({ profile, selectedSpot, push }: UploadVisitorsCSVModuleContainerProps) => {
    const { organization } = useOrganization();
    const uploadRef = useRef() as React.MutableRefObject<HTMLInputElement>;
    const [file, setFile] = useState<File | null>(null);
    const [code, setCode] = useState('');
    const [running, setRunning] = useState(false);
    const [finished, setFinished] = useState(false);
    const { uploadVisitorCSV, progressUploadVisitorCSV, errorMessage, uploadCsvProgress } = useVisitors();

    const uploadFile = (event: React.ChangeEvent<HTMLInputElement>) => {
        setFile(event.target.files?.[0] || null);
        //reset progress bar for a new file
        progressUploadVisitorCSV(selectedSpot);
    };

    const handleValidateVisitors = async () => {
        if (file === null) {
            return;
        }
        setRunning(true);
        //start progress bar when press validate button
        uploadVisitorCSV(code, file, selectedSpot);
    };

    const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
        event.preventDefault();
        const droppedFiles = event.dataTransfer.files;
        if (droppedFiles.length > 0) {
            setFile(droppedFiles[0]);
            progressUploadVisitorCSV(selectedSpot);
        }
    };

    const clearFile = () => {
        setFile(null);
    };

    const uploadAnotherCSV = () => {
        setFile(null);
        setCode('');
        setFinished(false);
    };

    useEffect(() => {
        trackPageViewed({
            pageName: 'Upload Visitors',
            organizationId: organization?.pk,
            spotId: selectedSpot,
            affiliationRole: organization?.role,
            superuser: profile.superuser,
        });
    }, [organization?.pk, organization?.role, selectedSpot, profile.superuser]);

    useEffect(() => {
        let intervalID: NodeJS.Timeout;
        //refresh progress bar until the process is done
        if (running && !errorMessage) {
            intervalID = setInterval(async () => {
                if (uploadCsvProgress.max > 0 && uploadCsvProgress.value === uploadCsvProgress.max) {
                    setRunning(false);
                    setFinished(true);
                }
                progressUploadVisitorCSV(selectedSpot);
            }, 2000);
        }
        if (errorMessage) {
            setRunning(false);
        }
        return () => clearInterval(intervalID);
    }, [running, organization, selectedSpot, progressUploadVisitorCSV, errorMessage, uploadCsvProgress, push]);

    return (
        <>
            <div className={styles.container}>
                <div>
                    <h3>Upload requirements</h3>
                    <ul>
                        <li>Your spreadsheet should be a CSV file</li>
                        <li>
                            Required columns: plate and state (must be short code or long name, e.g CA or California)
                        </li>
                        <li>Optional columns: name, phone, and country (must be two characters, e.g US)</li>
                        <li>All cars on the spreadsheet will be validated with the code entered below</li>
                    </ul>
                </div>

                <div>
                    <AGTAbleWrapper>
                        <thead>
                            <tr>
                                <th>Plate</th>
                                <th>State</th>
                                <th>Country</th>
                                <th>Name</th>
                                <th>Phone</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                <td>ABC123</td>
                                <td>California</td>
                                <td>US</td>
                                <td>John Doe</td>
                                <td>4155704151</td>
                            </tr>
                            <tr>
                                <td>DFG568</td>
                                <td>CA</td>
                                <td></td>
                                <td>Jane Doe</td>
                                <td></td>
                            </tr>
                            <tr>
                                <td>XYZ789</td>
                                <td>CA</td>
                                <td></td>
                                <td></td>
                                <td></td>
                            </tr>
                        </tbody>
                    </AGTAbleWrapper>
                </div>
                {errorMessage && <AGMessage color="error" title={errorMessage} className={styles.wideContainer} />}
                {finished && file ? (
                    <>
                        <AGMessage
                            color="success"
                            title="Visitors validated successfully"
                            className={styles.wideContainer}
                        >
                            <div className={styles.successMessageContainer}>
                                <p>
                                    {(file as any).name} with code {code}
                                </p>
                                <AGButton
                                    type="button"
                                    color="info"
                                    size="sm"
                                    onClick={() =>
                                        push(`/organization/${organization.pk}/spots/${selectedSpot}/visitors`)
                                    }
                                >
                                    View Visitors <ArrowIcon direction="right" />
                                </AGButton>
                            </div>
                        </AGMessage>
                        <AGButton type="button" color="primary" size="sm" onClick={uploadAnotherCSV}>
                            Upload Another CSV
                        </AGButton>
                    </>
                ) : (
                    <AGForm>
                        <div className={styles.smallContainer}>
                            <TextInput
                                label="Validation code"
                                placeholder="Enter your code"
                                onChange={(event) => setCode(event.target.value)}
                                errors={[]}
                                disabled={running}
                            />
                        </div>

                        <input ref={uploadRef} type="file" id="file" value={''} hidden onChange={uploadFile} />

                        {file ? (
                            !running || errorMessage ? (
                                <div className={`${styles.uploadContainer} ${styles.uploadContainerSuccess}`}>
                                    <span className={styles.closeIconButton} onClick={clearFile}>
                                        <XIcon />
                                    </span>
                                    <CheckCircleIcon />
                                    <h3>{(file as any).name}</h3>
                                    <p>File uploaded successfully</p>
                                </div>
                            ) : (
                                <div className={`${styles.uploadContainer} ${styles.uploadContainerValidating}`}>
                                    <h3>{(file as any).name}</h3>
                                    <p>Applying validation code {code} to visitors...</p>
                                    <ProgressBar value={uploadCsvProgress.value} total={uploadCsvProgress.max} />
                                </div>
                            )
                        ) : (
                            <div
                                className={styles.uploadContainer}
                                onDragOver={(event) => event.preventDefault()}
                                onDrop={handleDrop}
                            >
                                <UploadIcon />
                                <h3>Upload CSV File</h3>
                                <p>Drag and drop your CSV file here or click to browse</p>
                                <AGButton
                                    type="button"
                                    color="info"
                                    size="sm"
                                    onClick={() => uploadRef.current.click()}
                                >
                                    <FileIcon /> Choose file
                                </AGButton>
                            </div>
                        )}
                        {!running && (
                            <AGButton
                                type="button"
                                color="primary"
                                size="sm"
                                disabled={!file || !code}
                                onClick={handleValidateVisitors}
                            >
                                Validate Visitors
                            </AGButton>
                        )}
                    </AGForm>
                )}
            </div>
        </>
    );
};

function mapStateToProps(state: AppState) {
    return {
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        selectedSpot: selectedSpotId(state)!,
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        profile: state.auth.profile!,
    };
}

const mapDispatchToProps = {
    push,
};

export default connect(mapStateToProps, mapDispatchToProps)(UploadVisitorsCSVModuleContainer);
